import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import { AdminSection, OutletContext } from '@typings';
import { userSelector } from '@selectors';
import { useSelector } from '@hooks';
import { getNameInitials } from '@utils';

import { ChaseSpinner, Modal, Render, Table } from '@components';
import {
  AdminSectionClusterOrganizationActions,
  AdminSectionClusterTabButton,
} from '@components/AdminSection';
import { ManageClusterOrganizationModal } from '@components/Cluster';

const ITEMS_PER_PAGE = 50;

type HeaderCell = {
  text?: string;
  className?: string;
};

const HEADERS: HeaderCell[] = [
  { className: 'pl-10' },
  { text: 'Name' },
  { className: 'pr-10' },
];

export const AdminSectionClusterOrganizationsPage = () => {
  const { cluster, allClusterOrganizations, setCluster, setRightTabComponent } =
    useOutletContext<OutletContext.AdminSectionCluster>();

  const { systemRole } = useSelector(userSelector);

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const loaderRef = useRef<HTMLDivElement | null>(null);

  const orgsToShow = useMemo(() => {
    return cluster.orgs.slice(0, visibleCount);
  }, [cluster.orgs, visibleCount]);

  const handleOrgAdd = useCallback(
    (newOrg: AdminSection.ClusterOrganization) => {
      setCluster((prev) => {
        prev.orgs = [...(prev.orgs ?? []), newOrg];
      });
    },
    [setCluster],
  );

  const handleOrgUpdate = useCallback(
    (updatedOrg: AdminSection.ClusterOrganization) => {
      setCluster((prev) => {
        prev.orgs = prev.orgs.map((org) =>
          org.orgName === updatedOrg.orgName ? updatedOrg : org,
        );
      });
    },
    [setCluster],
  );

  const handleOrgRemove = useCallback(
    (orgName: AdminSection.ClusterOrganization['orgName']) => {
      setCluster((prev) => {
        prev.orgs = prev.orgs.filter((org) => org.orgName !== orgName);
      });
    },
    [setCluster],
  );

  useEffect(() => {
    if (
      systemRole === AdminSection.Role.User ||
      allClusterOrganizations.length === 0
    ) {
      return;
    }

    const orgs = allClusterOrganizations.filter(
      ({ orgName }) =>
        !cluster.orgs.some((clusterOrg) => clusterOrg.orgName === orgName),
    );

    setRightTabComponent(
      <div className="flex gap-3">
        <Modal
          content={
            <ManageClusterOrganizationModal
              orgs={orgs}
              clusterName={cluster.name}
              onOrgAdd={handleOrgAdd}
            />
          }
        >
          <AdminSectionClusterTabButton
            icon={faPlus}
            title="Add Organization"
          />
        </Modal>
      </div>,
    );

    return () => {
      setRightTabComponent(null);
    };
  }, [
    cluster.name,
    cluster,
    setRightTabComponent,
    systemRole,
    allClusterOrganizations.length,
    allClusterOrganizations,
    handleOrgAdd,
  ]);

  useEffect(() => {
    if (!loaderRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + ITEMS_PER_PAGE, cluster.orgs.length),
          );
        }
      },
      {
        rootMargin: '100px',
      },
    );

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [cluster.orgs.length]);

  return (
    <>
      <Table className="w-full auto-rows-min grid-cols-[min-content_1fr_min-content] overflow-auto rounded-none border-b border-stroke">
        <Table.Header>
          <Table.Row>
            {HEADERS.map(({ text, className }) => (
              <Table.Head key={text} className={`bg-background ${className}`}>
                {text}
              </Table.Head>
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {orgsToShow.map((org) => (
            <Table.Row key={org.orgName}>
              <Table.Cell>
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-subtle text-[12px] uppercase text-primary">
                  <span>{getNameInitials(org.orgName)}</span>
                </div>
              </Table.Cell>

              <Table.Cell>{org.orgName}</Table.Cell>

              <Table.Cell className="flex flex-row place-items-center gap-3">
                <Render if={systemRole !== AdminSection.Role.User}>
                  <AdminSectionClusterOrganizationActions
                    org={org}
                    clusterName={cluster.name}
                    onOrgUpdate={handleOrgUpdate}
                    onOrgRemove={handleOrgRemove}
                  />
                </Render>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <div ref={loaderRef} className="h-10" />

      <Render if={cluster.orgs.length > visibleCount}>
        <div className="flex w-full items-center justify-center">
          <ChaseSpinner color="black" className="h-8 w-8" />
        </div>
      </Render>

      <Render if={cluster.orgs.length === 0}>
        <p className="break-words pb-20 pt-10 text-center text-neural-04">
          No organizations found
        </p>
      </Render>
    </>
  );
};
